import { FC, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { CompanyDto } from 'app/api/apiClient/generated/types';
import { useMultiCompanyDetails } from 'app/api/hooks/core/v1/auth/user';
import Box from 'app/components/v2/components/Box';
import TypeAhead, { TypeAheadOption } from 'app/components/v2/components/TypeAhead';
import { useTypeAhead } from 'app/hooks';

import { useExternalUserCompanyJumper } from './hooks';

// Component Definition
const ExternalUserCompanyJumper: FC = () => {
  const { t } = useTranslation('components');
  const companyField = useTypeAhead('', null);
  const [isJumping, setIsJumping] = useState(false);
  const externalUserCompanyJumper = useExternalUserCompanyJumper();
  const multiCompanyDetails = useMultiCompanyDetails();
  const hasMultipleCompanyTypes = useMemo(() => {
    const distinctCompanyTypes = [...new Set(multiCompanyDetails.availableCompanies.map(c => c.type.name))];

    return distinctCompanyTypes.length > 1;
  }, [multiCompanyDetails]);

  const formatCompanyName = useCallback((company: CompanyDto) => {
    if (hasMultipleCompanyTypes && company.name !== '') {
      return `${company.name} (${company.type.name})`;
    }

    return company.name ?? '';
  }, [hasMultipleCompanyTypes]);

  const companyOptions = useMemo((): TypeAheadOption[] => {
    return multiCompanyDetails.availableCompanies
      .filter(x => !!x.id)
      .map<TypeAheadOption>(c => ({
        id: c.id?.toString() ?? '',
        label: formatCompanyName(c),
      }));
  }, [formatCompanyName, multiCompanyDetails]);

  const handleOptionSelected = useCallback((value: TypeAheadOption) => {
    setIsJumping(true);
    companyField.onTypeAheadSelect(value);
    externalUserCompanyJumper.jumpToCompany({
      companyId: value.id.toString(),
    });
  }, [companyField, externalUserCompanyJumper]);

  return (
    <Box
      alignItems="flex-end"
      display="flex"
      flexDirection="column"
      marginBottom={6}
    >
      <TypeAhead
        {...companyField}
        autoFocus
        disabled={isJumping}
        isFetching={isJumping}
        label={t('companyJumper.externalUserFindCompanyNameLabel', 'Companies')}
        onTypeAheadSelect={handleOptionSelected}
        options={companyOptions}
        showDropdownAfterInteraction
        showDropdownOnEmptyInput
        withClientSideFiltering
      />
    </Box>
  );
};

export default ExternalUserCompanyJumper;
